import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Archive, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

// Direct fetch wrapper for edge functions — supabase.functions.invoke has a
// short default timeout that's too short for the KIE vision calls (can take
// 60-120s). Using fetch with an explicit 180s timeout.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://wrlgoxbzlngdtomjhvnz.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function invokeEdgeFunction(name: string, body: Record<string, unknown>, timeoutMs = 180_000): Promise<any> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const { data: session } = await supabase.auth.getSession();
    const accessToken = session?.session?.access_token || SUPABASE_ANON_KEY;
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

interface PendingSubmission {
  id: string;
  name: string;
  status: string;
  thumbnail: string;
  updated: string;
  hash: string | null;
  best_match: {
    ig_post_id: string;
    ig_permalink: string;
    ig_thumbnail: string;
    ig_timestamp: string;
    ig_caption: string | null;
    hamming_distance: number;
    thumbnail_similarity: number;
    text_similarity: number;
    date_match: boolean;
    vision_match: boolean | null;
    vision_confidence: number | null;
    vision_reasoning: string | null;
    confidence: 'high' | 'medium' | 'low';
  } | null;
}

interface PostedVideoCheckModalProps {
  artistId: string;
  artistName: string;
  onClose: () => void;
  onResolved?: () => void;
}

export const PostedVideoCheckModal: React.FC<PostedVideoCheckModalProps> = ({
  artistId,
  artistName,
  onClose,
  onResolved,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<PendingSubmission[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [acting, setActing] = useState(false);
  const [resolvedCount, setResolvedCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, [artistId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await invokeEdgeFunction('check-posted-videos', { artist_id: artistId, days_back: 15 });

      if (data?.error) throw new Error(data.error);

      // Only keep submissions with a real match
      // Exclude if vision explicitly says no match, otherwise require high/medium confidence or vision match
      const withMatch = (data.submissions || []).filter((s: any) => {
        if (!s.best_match) return false;
        // If vision ran and said no match, exclude regardless of dHash confidence
        if (s.best_match.vision_match === false) return false;
        if (s.best_match.confidence === 'high' || s.best_match.confidence === 'medium') return true;
        if (s.best_match.vision_match === true) return true;
        return false;
      });

      // Sort: highest confidence matches first
      // Order: high → medium → low → no match
      const confidenceRank = { high: 0, medium: 1, low: 2 };
      const sorted = [...withMatch].sort((a, b) => {
        const aConf = a.best_match?.confidence || 'low';
        const bConf = b.best_match?.confidence || 'low';
        const aRank = confidenceRank[aConf as keyof typeof confidenceRank] ?? 3;
        const bRank = confidenceRank[bConf as keyof typeof confidenceRank] ?? 3;
        if (aRank !== bRank) return aRank - bRank;
        // Within same confidence, sort by vision_match=true first, then by thumbnail_similarity
        const aVision = a.best_match?.vision_match === true ? 1 : 0;
        const bVision = b.best_match?.vision_match === true ? 1 : 0;
        if (aVision !== bVision) return bVision - aVision;
        const aThumb = a.best_match?.thumbnail_similarity || 0;
        const bThumb = b.best_match?.thumbnail_similarity || 0;
        return bThumb - aThumb;
      });

      setSubmissions(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: 'mark-posted' | 'discard' | 'keep') => {
    const submission = submissions[currentIndex];
    if (!submission) return;

    setActing(true);
    try {
      await invokeEdgeFunction('check-posted-videos', { artist_id: artistId, action, submission_id: submission.id }, 30_000);

      const actionLabels: Record<string, string> = {
        'mark-posted': 'Als gepostet markiert',
        'discard': 'Idee verworfen',
        'keep': 'Wird noch gepostet',
      };
      toast.success(`${submission.name}: ${actionLabels[action]}`);

      setResolvedCount((c) => c + 1);

      // Move to next submission or close
      if (currentIndex < submissions.length - 1) {
        setCurrentIndex((i) => i + 1);
      } else {
        // All done
        onResolved?.();
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return null;
  }

  if (error || submissions.length === 0) {
    // No matches or error — don't show the modal at all
    return null;
  }

  const submission = submissions[currentIndex];
  const match = submission.best_match;

  const confidenceColor = (conf: string) => {
    if (conf === 'high') return 'text-green-400 bg-green-400/10 border-green-400/30';
    if (conf === 'medium') return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
    return 'text-gray-400 bg-gray-400/10 border-gray-400/30';
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-white">
              Bereits gepostet? — {artistName}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Video {currentIndex + 1} von {submissions.length}
              {resolvedCount > 0 && ` · ${resolvedCount} erledigt`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content: Left = correction-needed video, Right = IG posts */}
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Pending submission */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Correction Needed
              </h3>
              <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
                {submission.thumbnail ? (
                  <img
                    src={submission.thumbnail}
                    alt={submission.name}
                    className="w-full aspect-video object-cover"
                  />
                ) : (
                  <div className="w-full aspect-video bg-gray-700 flex items-center justify-center">
                    <AlertCircle className="h-10 w-10 text-gray-500" />
                  </div>
                )}
                <div className="p-3">
                  <p className="font-medium text-white text-sm truncate">{submission.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Updated: {new Date(submission.updated).toLocaleDateString('de-DE')}
                  </p>
                  <span className="inline-block mt-2 px-2 py-0.5 text-xs rounded-full bg-orange-400/10 text-orange-400 border border-orange-400/30">
                    {submission.status}
                  </span>
                </div>
              </div>

            </div>

            {/* Right: Matched IG post */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                {match ? 'Gefundener Instagram Post' : 'Instagram Post'}
              </h3>
              {match ? (
                <div className="bg-gray-800 rounded-lg overflow-hidden border border-green-400/50">
                  <a
                    href={match.ig_permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block relative group"
                  >
                    <img
                      src={match.ig_thumbnail}
                      alt="IG Post"
                      className="w-full aspect-video object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-green-400 rounded-full p-1">
                      <CheckCircle2 className="h-4 w-4 text-black" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-xs text-white">
                        {new Date(match.ig_timestamp).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>
                    </div>
                  </a>
                  <div className="p-3">
                    <p className="text-xs text-gray-400 line-clamp-3">
                      {match.ig_caption || 'Kein Caption'}
                    </p>
                    <a
                      href={match.ig_permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-green-400 underline hover:no-underline"
                    >
                      IG Post öffnen <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 text-center">
                  <AlertCircle className="h-10 w-10 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Kein passender IG Post gefunden</p>
                </div>
              )}
            </div>
          </div>

          {/* Match indicator — full width below */}
          {match && (
            <div className={`mt-4 p-3 rounded-lg border text-sm ${confidenceColor(match.confidence)}`}>
              <p className="font-medium">
                {match.vision_match === true && match.vision_confidence && match.vision_confidence > 0.8
                  ? '✓ KI-Vision: Selbes Video erkannt'
                  : match.confidence === 'high'
                  ? '✓ Sehr wahrscheinlicher Match'
                  : match.confidence === 'medium'
                  ? '⚠ Möglicher Match'
                  : '? Schwacher Match'}
              </p>
              <div className="mt-1 text-xs space-y-0.5 opacity-80">
                {match.vision_match !== null && (
                  <p>KI-Vision: {match.vision_match ? '✓ Match' : '✗ kein Match'} ({Math.round((match.vision_confidence || 0) * 100)}%)</p>
                )}
                <p>Text: {Math.round(match.text_similarity * 100)}% ähnlich</p>
                <p>Datum: {match.date_match ? '✓ passt' : '✗ abweichend'}</p>
              </div>
              {match.vision_reasoning && (
                <p className="mt-1.5 text-xs italic opacity-70 border-t border-current/20 pt-1.5">
                  {match.vision_reasoning}
                </p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => handleAction('mark-posted')}
              disabled={acting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
            >
              <CheckCircle2 className="h-5 w-5" />
              Bereits gepostet
            </button>
            <button
              onClick={() => handleAction('discard')}
              disabled={acting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
            >
              <Archive className="h-5 w-5" />
              Idee verworfen
            </button>
            <button
              onClick={() => handleAction('keep')}
              disabled={acting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-600 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
            >
              <Clock className="h-5 w-5" />
              Wird noch gepostet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
